import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, ShieldOff, X, Square, RotateCcw, Save, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/config";

/**
 * PDF anonymization editor.
 *
 * Uses pdfjs-dist to render each PDF page in a canvas, lets the admin draw
 * redaction rectangles on any page, then POSTs the normalized coordinates
 * to `POST /api/admin/documents/{doc_id}/anonymize-pdf` which uses PyMuPDF
 * to apply true redactions (text stripped, not just visually covered → GDPR-friendly).
 *
 * The server saves a new document ("ANON - <original>.pdf") which can be
 * downloaded/shared with clients safely.
 *
 * Props:
 *   doc: { doc_id, original_filename }
 *   candidateName: string (display only)
 *   onSaved: (newDocId) => void — called after successful save with the new anonymized doc_id
 *   onClose: () => void
 */
export default function PDFAnonymizer({ doc, candidateName, onSaved, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfDoc, setPdfDoc] = useState(null); // pdfjs PDFDocumentProxy
  const [pageIndex, setPageIndex] = useState(0); // 0-based
  const [pageRects, setPageRects] = useState({}); // { pageIndex: [{x,y,w,h} normalized 0..1] }
  const [drawing, setDrawing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [renderScale, setRenderScale] = useState(1.4);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const currentRenderTask = useRef(null);

  // Load PDF via pdfjs-dist
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
        // Configure worker — bundled with pdfjs-dist as pdf.worker.min.mjs
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/build/pdf.worker.min.mjs",
          import.meta.url,
        ).toString();

        const r = await axios.get(`${API}/documents/${doc.doc_id}/download`, {
          withCredentials: true,
          responseType: "arraybuffer",
        });
        if (cancelled) return;
        const loadingTask = pdfjs.getDocument({ data: r.data });
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        setPdfDoc(pdf);
        setPageIndex(0);
      } catch (e) {
        setError(e.message || "Impossible de charger le PDF");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.doc_id]);

  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;
    // Cancel any in-flight render (page changes fast)
    try { currentRenderTask.current?.cancel(); } catch { /* ignore */ }
    const page = await pdfDoc.getPage(pageIndex + 1);
    const viewport = page.getViewport({ scale: renderScale });
    const canvas = canvasRef.current;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    const ctx = canvas.getContext("2d");
    const task = page.render({ canvasContext: ctx, viewport });
    currentRenderTask.current = task;
    try {
      await task.promise;
    } catch (e) {
      if (e?.name !== "RenderingCancelledException") throw e;
    }
    // Match overlay canvas size to the rendered page
    const ov = overlayCanvasRef.current;
    if (ov) {
      ov.width = viewport.width;
      ov.height = viewport.height;
      ov.style.width = `${viewport.width}px`;
      ov.style.height = `${viewport.height}px`;
    }
    drawOverlay();
  }, [pdfDoc, pageIndex, renderScale]);

  useEffect(() => { renderPage(); }, [renderPage]);

  const drawOverlay = useCallback(() => {
    const ov = overlayCanvasRef.current;
    if (!ov) return;
    const ctx = ov.getContext("2d");
    ctx.clearRect(0, 0, ov.width, ov.height);
    const rects = pageRects[pageIndex] || [];
    ctx.fillStyle = "rgba(0,0,0,0.85)";
    ctx.strokeStyle = "#662483";
    ctx.lineWidth = 2;
    for (const r of rects) {
      const x = r.x * ov.width;
      const y = r.y * ov.height;
      const w = r.width * ov.width;
      const h = r.height * ov.height;
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
    }
    if (drawing) {
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(
        drawing.startX,
        drawing.startY,
        drawing.currentX - drawing.startX,
        drawing.currentY - drawing.startY,
      );
      ctx.setLineDash([]);
    }
  }, [pageRects, pageIndex, drawing]);

  useEffect(() => { drawOverlay(); }, [drawOverlay]);

  const onMouseDown = (e) => {
    if (!overlayCanvasRef.current) return;
    const r = overlayCanvasRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    setDrawing({ startX: x, startY: y, currentX: x, currentY: y });
  };
  const onMouseMove = (e) => {
    if (!drawing || !overlayCanvasRef.current) return;
    const r = overlayCanvasRef.current.getBoundingClientRect();
    setDrawing((d) => ({ ...d, currentX: e.clientX - r.left, currentY: e.clientY - r.top }));
  };
  const onMouseUp = () => {
    if (!drawing || !overlayCanvasRef.current) { setDrawing(null); return; }
    const ov = overlayCanvasRef.current;
    const { startX, startY, currentX, currentY } = drawing;
    const x = Math.min(startX, currentX) / ov.width;
    const y = Math.min(startY, currentY) / ov.height;
    const width = Math.abs(currentX - startX) / ov.width;
    const height = Math.abs(currentY - startY) / ov.height;
    if (width > 0.005 && height > 0.005) {
      setPageRects((prev) => ({
        ...prev,
        [pageIndex]: [...(prev[pageIndex] || []), { x, y, width, height }],
      }));
    }
    setDrawing(null);
  };

  const clearCurrentPage = () => {
    setPageRects((prev) => ({ ...prev, [pageIndex]: [] }));
  };
  const clearAll = () => setPageRects({});
  const undo = () => {
    setPageRects((prev) => {
      const arr = [...(prev[pageIndex] || [])];
      arr.pop();
      return { ...prev, [pageIndex]: arr };
    });
  };

  const totalRects = Object.values(pageRects).reduce((sum, arr) => sum + arr.length, 0);

  const save = async () => {
    if (totalRects === 0) { toast.error("Tracez au moins un rectangle avant d'enregistrer"); return; }
    setSaving(true);
    try {
      const payload = {
        rectangles: Object.entries(pageRects).flatMap(([p, arr]) =>
          arr.map((r) => ({ page: Number(p), ...r })),
        ),
      };
      const r = await axios.post(
        `${API}/admin/documents/${doc.doc_id}/anonymize-pdf`,
        payload,
        { withCredentials: true },
      );
      toast.success(`PDF anonymisé (${totalRects} zones masquées)`);
      // Télécharge automatiquement le PDF anonymisé sur le PC de l'admin,
      // en plus de son enregistrement dans la fiche candidat.
      try {
        const dl = await axios.get(`${API}/documents/${r.data.doc_id}/download`, {
          withCredentials: true,
          responseType: "blob",
        });
        const url = URL.createObjectURL(dl.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = r.data.original_filename || "document_anonymise.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (dlErr) {
        // L'anonymisation a réussi côté serveur même si le téléchargement
        // auto échoue (ex. réseau) — l'admin pourra le retélécharger depuis
        // la fiche candidat, donc on ne bloque pas sur cette erreur.
        toast.error("PDF anonymisé enregistré, mais le téléchargement automatique a échoué. Retéléchargez-le depuis la fiche candidat.");
      }
      onSaved?.(r.data.doc_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'anonymisation");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center gap-3 text-gray-600" data-testid="pdf-anonymizer-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#662483]" />
        <span className="text-sm">Chargement du PDF…</span>
      </div>
    );
  }
  if (error || !pdfDoc) {
    return (
      <div className="p-8 text-center">
        <ShieldOff className="w-10 h-10 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-600">{error || "PDF illisible"}</p>
      </div>
    );
  }

  const pageCount = pdfDoc.numPages;

  return (
    <div className="flex flex-col h-full" data-testid="pdf-anonymizer">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 p-3 bg-[#FAFAFA] flex-wrap">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setPageIndex((p) => Math.max(0, p - 1))} disabled={pageIndex === 0} data-testid="pdf-prev-page">
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <span className="text-xs font-semibold text-[#3F3F3F]" data-testid="pdf-page-indicator">
            Page {pageIndex + 1} / {pageCount}
          </span>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))} disabled={pageIndex >= pageCount - 1} data-testid="pdf-next-page">
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
          <select value={renderScale} onChange={(e) => setRenderScale(Number(e.target.value))} className="text-xs h-8 border border-gray-300 rounded-none px-2" title="Zoom">
            <option value={1}>100 %</option>
            <option value={1.2}>120 %</option>
            <option value={1.4}>140 %</option>
            <option value={1.75}>175 %</option>
            <option value={2}>200 %</option>
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Square className="w-3.5 h-3.5 text-[#662483]" />
          <span data-testid="pdf-rect-count"><strong>{(pageRects[pageIndex] || []).length}</strong> sur cette page · <strong>{totalRects}</strong> au total</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={undo} disabled={(pageRects[pageIndex] || []).length === 0} className="rounded-none h-8 text-xs" data-testid="pdf-undo">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Annuler
          </Button>
          <Button size="sm" variant="outline" onClick={clearCurrentPage} disabled={(pageRects[pageIndex] || []).length === 0} className="rounded-none h-8 text-xs">
            Effacer page
          </Button>
          <Button size="sm" variant="outline" onClick={clearAll} disabled={totalRects === 0} className="rounded-none h-8 text-xs text-red-600 border-red-300">
            Tout effacer
          </Button>
          <Button size="sm" onClick={save} disabled={saving || totalRects === 0} className="rounded-none h-8 bg-gradient-to-r from-[#662483] to-[#0069B4] text-white text-xs font-semibold" data-testid="pdf-save-anon">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            {saving ? "Enregistrement…" : "Enregistrer PDF anonymisé"}
          </Button>
        </div>
      </div>

      {/* Canvas area */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-200 p-4 flex items-start justify-center">
        <div className="relative shadow-lg" data-testid="pdf-canvas-wrap">
          <canvas ref={canvasRef} className="block bg-white" />
          <canvas
            ref={overlayCanvasRef}
            className="absolute top-0 left-0 cursor-crosshair"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            data-testid="pdf-overlay-canvas"
          />
        </div>
      </div>

      <div className="border-t border-gray-200 px-3 py-2 text-xs text-gray-500 flex items-center justify-between">
        <span>💡 Tracez des rectangles sur les infos sensibles (nom, adresse, téléphone…). Les textes sous les rectangles seront <strong>totalement supprimés</strong> du PDF.</span>
        {onClose && (
          <button onClick={onClose} className="text-gray-500 hover:text-[#3F3F3F] px-2 py-1" data-testid="pdf-anon-close">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
